import * as React from 'react';
import * as echarts from 'echarts/core';
import { FunnelChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';

import type {
  TitleComponentOption,
  TooltipComponentOption,
  LegendComponentOption,
  FunnelSeriesOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
  | TitleComponentOption
  | TooltipComponentOption
  | LegendComponentOption
  | FunnelSeriesOption
>;

echarts.use([
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  FunnelChart,
  LabelLayout,
  UniversalTransition,
  SVGRenderer,
]);

export type SocialFunnelTotals = {
  redirect_landings: number;
  store_redirects: number;
  store_acquisitions?: number;
  attributed_installs: number;
  visits: number;
  inquiries: number;
};

const SocialCampaignFunnelChart: React.FC<{ totals: SocialFunnelTotals }> = ({ totals }) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);

  React.useEffect(() => {
    const storeAcquired =
      totals.store_acquisitions || totals.attributed_installs || 0;
    const appOpens = totals.attributed_installs || 0;
    const webVisits = Math.max(totals.visits || 0, appOpens);

    const data = [
      { name: 'Redirect', value: totals.redirect_landings || 0 },
      { name: 'Store', value: totals.store_redirects || 0 },
      { name: 'Store acquired', value: storeAcquired },
      { name: 'App opens', value: appOpens },
      { name: 'Web visits', value: webVisits },
      { name: 'Inquiries', value: totals.inquiries || 0 },
    ];

    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }

      const option: ECOption = {
        tooltip: {
          trigger: 'item',
          formatter: (p: any) => `${p.name}: ${p.value}`,
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: data.map((d) => d.name),
        },
        series: [
          {
            name: 'Campaign funnel',
            type: 'funnel',
            left: '25%',
            top: 20,
            bottom: 20,
            width: '60%',
            sort: 'none',
            gap: 4,
            label: {
              show: true,
              formatter: '{b}: {c}',
            },
            data,
          },
        ],
      };

      chartInstance.current.setOption(option);
    }

    const resizeHandler = () => chartInstance.current?.resize();
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [totals]);

  return <div ref={chartRef} style={{ width: '100%', height: 320 }} />;
};

export default SocialCampaignFunnelChart;
